'use client'

import { CollectionEmptyProductList } from '@/app/[locale]/collection/_components/empty-product-list'
import { TrackingInsiderObject } from '@/components/analytics/tracking-insider-object'
import { GiftShopProductCard } from '@/components/common/product-card/gift-shop-product-card'
import { ProductCardSkeleton } from '@/components/common/product-card/product-item-skeleton'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { useCartQuery } from '@/hooks/react-query/cart/use-cart'
import { useProductListByIds } from '@/hooks/react-query/products/use-product'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn } from '@/lib/utils'
import { TCollectionData } from '@/types/collection'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useSearchParams } from 'next/navigation'
import { useMemo } from 'react'
import { useCollectionData } from '../../../hooks/use-collection-data'

type Props = {
  className?: string
  serverData: TCollectionData
  initialFilters?: Record<string, string | string[] | number | undefined>
}

export const GiftShopListing = ({ className, serverData }: Props) => {
  const t = useTranslations()
  const searchParams = useSearchParams()
  const mainProductId = searchParams.get('main_product_id')

  const {
    data,
    isLoading,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useCollectionData({ serverData })

  // Fetch cart data to check selected gifts
  const { data: cartData } = useCartQuery()

  const allProducts = useMemo(
    () => data?.pages.flatMap((page) => page?.products ?? []) ?? [],
    [data?.pages]
  )

  // Find main product from cart and extract selected gift IDs
  const { mainProduct, selectedGiftIds } = useMemo(() => {
    if (!mainProductId || !cartData?.items) {
      return { mainProduct: null, selectedGiftIds: [] }
    }

    const main = cartData.items.find((item) => item.index_id === mainProductId)

    const selectedIds = main?.nestedGift?.map((gift) => gift.id) ?? []

    return {
      mainProduct: main || null,
      selectedGiftIds: selectedIds,
    }
  }, [mainProductId, cartData])

  // Pre-fetch selected gifts to show them at the top even if they're on later pages
  const { data: selectedGiftsData } = useProductListByIds(
    selectedGiftIds,
    undefined, // no limit
    selectedGiftIds.length > 0 && !!mainProductId // only fetch when needed
  )

  // Merge selected gifts with paginated products
  const sortedProducts = useMemo(() => {
    const selectedGifts = selectedGiftsData || []

    // If no selected gifts, return allProducts as-is
    if (
      !mainProductId ||
      !mainProduct ||
      selectedGiftIds.length === 0 ||
      selectedGifts.length === 0
    ) {
      // Still sort if selected gifts exist in current page
      if (selectedGiftIds.length > 0) {
        const selected: typeof allProducts = []
        const unselected: typeof allProducts = []

        allProducts.forEach((product) => {
          if (selectedGiftIds.includes(product.id)) {
            selected.push(product)
          } else {
            unselected.push(product)
          }
        })

        return [...selected, ...unselected]
      }
      return allProducts
    }

    // Get IDs of selected gifts that are already in allProducts (from pagination)
    const selectedGiftIdsInList = new Set(
      allProducts.filter((p) => selectedGiftIds.includes(p.id)).map((p) => p.id)
    )

    // Filter out selected gifts that are already in pagination results to avoid duplicates
    const selectedGiftsNotInList = selectedGifts.filter(
      (gift) => !selectedGiftIdsInList.has(gift.id)
    )

    // Split paginated products into selected and unselected groups
    const selectedInList: typeof allProducts = []
    const unselected: typeof allProducts = []

    allProducts.forEach((product) => {
      if (selectedGiftIds.includes(product.id)) {
        selectedInList.push(product)
      } else {
        unselected.push(product)
      }
    })

    // Combine: prepend selected gifts not in list, then selected in list, then unselected
    // This ensures selected gifts always appear first, even if they're on later pages
    return [...selectedGiftsNotInList, ...selectedInList, ...unselected]
  }, [
    allProducts,
    mainProductId,
    mainProduct,
    selectedGiftIds,
    selectedGiftsData,
  ])
  if (!serverData) {
    return null
  }

  const pagination = data?.pages?.[data.pages.length - 1]?.pagination

  // Show loading state when:
  // 1. Initial loading (isLoading && no products)
  // 2. Refetching with no products (isFetching && no products)
  // 3. Refetching with products (isFetching && allProducts.length > 0)
  const shouldShowLoading =
    (isLoading && allProducts.length === 0) ||
    (isFetching && allProducts.length === 0) ||
    (isFetching && allProducts.length > 0 && !isFetchingNextPage)

  if (shouldShowLoading) {
    return (
      <div className='w-full flex-1' id='collection-listing'>
        <div
          className={cn(
            'grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-5',
            className
          )}
        >
          {Array.from({ length: 12 }).map((_, index) => (
            <ProductCardSkeleton key={index} />
          ))}
        </div>
      </div>
    )
  }

  const insiderObjectListing = {
    items: sortedProducts.map((product) => ({
      id: product.id,
      name: product.title,
      currency: 'VND',
      taxonomy: [],
      unit_price: product.compare_price,
      unit_sale_price: product.regular_price,
      url: `https://www.coolmate.me${product.href}`,
      product_image_url: ImageHelper.getImageUrl(product.default_thumbnail)
        .replace(/\(/g, '%28')
        .replace(/\)/g, '%29'),
    })),
  }

  const currentShowing = allProducts.length
  const totalProducts = pagination?.total ?? 0

  return (
    <>
      <div className='w-full flex-1' id='collection-listing'>
        {/* Alert nếu không có main_product_id */}
        {!mainProductId && (
          <Alert
            variant='neutral'
            size='md'
            title={
              t('gift_shop.please_select_from_cart') ||
              'Vui lòng chọn sản phẩm từ giỏ hàng'
            }
            className='mb-4'
          />
        )}
        <div
          className={cn(
            'grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-5',
            className
          )}
        >
          {sortedProducts.map((product, index) => (
            <GiftShopProductCard
              product={product}
              key={`${product.id}-${index}`}
            />
          ))}
          {allProducts.length === 0 && !isFetching && !isFetchingNextPage && (
            <CollectionEmptyProductList />
          )}
          {isFetchingNextPage &&
            Array.from({ length: 4 }).map((_, index) => (
              <ProductCardSkeleton key={`loading-${index}`} />
            ))}
        </div>
        {hasNextPage && (
          <div className='mt-10 text-center lg:mt-17'>
            <Button
              variant='black'
              className='group'
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
            >
              {t('button.view_more')}{' '}
              <Image
                src='/icons/icon-arrow-line-right.svg'
                width={16}
                height={19}
                alt='icon arrow right'
                className='mb-1 h-5 w-4 transition-transform duration-200 group-hover:translate-x-1'
              />
            </Button>
            <p className='mt-1 font-sans text-sm leading-4.5 text-neutral-900/60 lg:mt-1.5 lg:text-base lg:leading-5'>
              {t('collection_page.message.display_product_count', {
                currentShowing,
                totalProducts,
              })}
            </p>
          </div>
        )}
      </div>
      <TrackingInsiderObject keyName='listing' value={insiderObjectListing} />
    </>
  )
}
